import axios from "axios";
import { isBagelAdmin } from "../utils";
import { CurrentUser } from "./auth";

const API_DOMAIN = window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN;

// Debug: This should show up in console immediately when file loads

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatRequest {
  agent: 'account-setup-agent';
  message: string;
  conversation?: ChatMessage[];
}

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

export interface ConversationLength {
  messageCount: number;
  estimatedTokens: number;
}

export interface FunctionCall {
  name: string;
  result: { 
    success: boolean; 
    data: any;
  };
}

export interface ChatResponseData {
  response: string;
  tokenUsage: TokenUsage;
  conversationLength: ConversationLength;
  functionCalls?: FunctionCall[];
}

export interface ChatResponse {
  success: boolean;
  data?: ChatResponseData;
  error?: string;
}

export const sendChatMessage = async (
  request: ChatRequest,
  currentUser: CurrentUser | null
): Promise<ChatResponse> => {
  
  try {
    // Check if user is Bagel Admin
  
    if (!isBagelAdmin(currentUser)) {
      console.error('❌ [Chat API] Access denied - user is not Bagel Admin');
      throw new Error('Access denied. Bagel Admin privileges required.');
    }
    
    // Validate required parameters
    if (!request.agent || request.agent !== 'account-setup-agent') {
      console.error('❌ [Chat API] Invalid agent parameter:', request.agent);
      throw new Error('Agent parameter is required and must be "account-setup-agent"');
    }

    if (!request.message || typeof request.message !== 'string' || request.message.trim() === '') {
      console.error('❌ [Chat API] Invalid message parameter:', request.message);
      throw new Error('Message parameter is required and must be a non-empty string');
    }
    // Add Content-Type header (Origin header automatically handled by browser)
    const config = { headers: { 'Content-Type': 'application/json' } };
    const url = `${API_DOMAIN}/agents/chat`;
    
    // TEMPORARY: Mock response disabled - using real backend now
    // TODO: Remove this mock code entirely once confirmed working
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('🎭 [Chat API] Using mock response for development');
    //   // ... mock code commented out
    // }

    // Make API request (will be used once backend is ready)
    const response = await axios.post(url, request, config);
    
    
    
    return response.data;
  } catch (error: any) {
    console.error('❌ [Chat API] Error occurred:', error);
    
    // Handle different error types
    if (error.response) {
      // API returned an error response
      const { status, data } = error.response;
      
      console.error('🚨 [Chat API] HTTP Error Response:', {
        status,
        statusText: error.response.statusText,
        data,
        url: error.response.config?.url
      });
      
      if (status === 403) {
        console.error('🔒 [Chat API] 403 Forbidden - Admin access required');
        return {
          success: false,
          error: 'Access denied. Bagel Admin privileges required.'
        };
      } else if (status === 400) {
        console.error('📝 [Chat API] 400 Bad Request - Invalid parameters');
        return {
          success: false,
          error: data.error || 'Invalid request parameters'
        };
      } else if (status === 404) {
        console.error('🔍 [Chat API] 404 Not Found - Endpoint does not exist');
        return {
          success: false,
          error: 'Chat service not available. Please contact support.'
        };
      } else if (status === 500) {
        console.error('💥 [Chat API] 500 Server Error - Internal server error');
        return {
          success: false,
          error: 'An error occurred while processing your request. Please try again later.'
        };
      } else {
        console.error(`🚨 [Chat API] ${status} Error - Unexpected status code`);
        return {
          success: false,
          error: data.error || `Server error: ${status}`
        };
      }
    } else if (error.request) {
      // Network error
      console.error('🌐 [Chat API] Network Error:', {
        message: error.message,
        code: error.code,
        request: error.request
      });
      return {
        success: false,
        error: 'Network error. Please check your connection and try again.'
      };
    } else {
      // Other error (validation, etc.)
      console.error('⚠️ [Chat API] Other Error:', error.message);
      return {
        success: false,
        error: error.message || 'An unexpected error occurred'
      };
    }
  }
};
